/**
 * Page Editor
 * Editor component for page content with sections and blocks
 */

import * as React from 'react';
import {
    Card,
    CardContent,
    Button,
    Input,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    cn,
} from '@ai-content-hub/content-kit';
import { generateId } from '@ai-content-hub/content-core';
import type { LayoutConfig } from '@ai-content-hub/content-core';
import type { ElementRuntimeConfigSlice } from '@ai-content-hub/content-kit';
import { BlockTypeSelector } from '../block/BlockTypeSelector';
import { BlockEditor } from '../block/BlockEditor';
import { createDefaultBlockContent } from '../block/types';
import type { BlockContent, BlockContentType } from '../block/types';
import type { PageElementData, PageSection, PageBlock } from './schema';
import { sectionLayoutPresets, createSection, createBlock, pageElementSchema } from './schema';
import {
    Plus,
    Trash2,
    GripVertical,
    ChevronDown,
    ChevronUp,
    LayoutGrid,
    Columns,
    Rows,
    Square,
    Settings2,
    Eye,
    EyeOff,
} from 'lucide-react';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';
import { useElementUITranslations } from '../i18n/elementUI';

export interface PageEditProps {
    /** Page data */
    value: PageElementData;
    /** Callback when data changes */
    onChange: (value: PageElementData) => void;
    /** Whether editing is disabled */
    disabled?: boolean;
    /** Hide the page settings card (title/description) */
    hideSettings?: boolean;
    /** Restrict which block content types are available (e.g. ['rich-text', 'image', 'audio']) */
    allowedBlockTypes?: BlockContentType[];
    /** Parent content config (i18n, …) */
    config?: ElementRuntimeConfigSlice;
    /** Overrides config.i18n.defaultLanguage when set */
    language?: string;
    /** Additional CSS class */
    className?: string;
}

type LayoutPresetKey = keyof typeof sectionLayoutPresets;

const layoutOptions: { value: LayoutPresetKey; label: string; icon: React.ReactNode }[] = [
    { value: 'single', label: 'Single', icon: <Square className="h-4 w-4" /> },
    { value: 'vertical', label: 'Vertical', icon: <Rows className="h-4 w-4" /> },
    { value: 'horizontal', label: 'Horizontal', icon: <Columns className="h-4 w-4" /> },
    { value: 'grid2', label: '2 Columns', icon: <LayoutGrid className="h-4 w-4" /> },
    { value: 'grid3', label: '3 Columns', icon: <LayoutGrid className="h-4 w-4" /> },
    { value: 'grid4', label: '4 Columns', icon: <LayoutGrid className="h-4 w-4" /> },
];

/**
 * Block Editor within a section
 */
const BlockItem: React.FC<{
    block: PageBlock;
    index: number;
    totalBlocks: number;
    disabled: boolean;
    allowedBlockTypes?: BlockContentType[];
    onUpdate: (block: PageBlock) => void;
    onRemove: () => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
}> = ({
    block,
    index,
    totalBlocks,
    disabled,
    allowedBlockTypes,
    onUpdate,
    onRemove,
    onMoveUp,
    onMoveDown,
}) => {
    const et = useElementUITranslations();
    const [isExpanded, setIsExpanded] = React.useState(true);

    const handleContentChange = (content: BlockContent) => {
        onUpdate({ ...block, content });
    };

    const handleTypeChange = (type: BlockContentType) => {
        if (block.content.type === type) return;
        onUpdate({ ...block, content: createDefaultBlockContent(type) });
    };

    const isShowAfterSubmitted = block.settings?.showAfterSubmitted ?? false;

    const handleToggleShowAfterSubmitted = () => {
        onUpdate({
            ...block,
            settings: {
                ...block.settings,
                showAfterSubmitted: !isShowAfterSubmitted,
            },
        });
    };

    return (
        <div className="page-edit-block-item rounded-lg overflow-hidden border border-border-surface bg-background shadow-sm">
            {/* Block Header */}
            <div
                className="page-edit-block-header flex items-center gap-2 px-3 py-2.5 sm:px-4 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <GripVertical className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground">{et.block} {index + 1}</span>
                <span className="text-xs text-muted-foreground capitalize">
                    ({block.content.type})
                </span>
                {isShowAfterSubmitted && (
                    // EAA-1674: raw text-amber-600 → text-muted-foreground so
                    // this informational badge follows global design tokens,
                    // matching the sibling meta spans in this header row.
                    <span className="text-xs text-muted-foreground font-medium">(after submit)</span>
                )}
                <div className="flex-1" />
                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => {
                            e.stopPropagation();
                            onMoveUp();
                        }}
                        disabled={disabled || index === 0}
                    >
                        <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => {
                            e.stopPropagation();
                            onMoveDown();
                        }}
                        disabled={disabled || index === totalBlocks - 1}
                    >
                        <ChevronDown className="h-3 w-3" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                            'h-6 w-6',
                            // EAA-1674: raw amber palette → text-warning (not
                            // text-primary: dark theme's --primary equals
                            // --foreground, which would make this toggle's
                            // active state invisible in dark mode).
                            isShowAfterSubmitted && 'text-warning hover:text-warning/80'
                        )}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleToggleShowAfterSubmitted();
                        }}
                        disabled={disabled}
                        title={
                            isShowAfterSubmitted
                                ? 'Shown after submit (click to always show)'
                                : 'Always visible (click to show after submit only)'
                        }
                    >
                        {isShowAfterSubmitted ? (
                            <EyeOff className="h-3 w-3" />
                        ) : (
                            <Eye className="h-3 w-3" />
                        )}
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-destructive hover:text-destructive"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove();
                        }}
                        disabled={disabled}
                    >
                        <Trash2 className="h-3 w-3" />
                    </Button>
                </div>
            </div>

            {/* Block Content */}
            {isExpanded && (
                <div className="page-edit-block-content p-4 sm:p-5 space-y-3">
                    <BlockTypeSelector
                        value={block.content.type}
                        onChange={handleTypeChange}
                        disabled={disabled}
                        allowedTypes={allowedBlockTypes}
                        size="sm"
                    />
                    <BlockEditor
                        content={block.content}
                        onChange={handleContentChange}
                        disabled={disabled}
                    />
                </div>
            )}
        </div>
    );
};

/**
 * Section Editor
 */
const SectionItem: React.FC<{
    section: PageSection;
    index: number;
    totalSections: number;
    disabled: boolean;
    defaultLayout: LayoutConfig;
    allowedBlockTypes?: BlockContentType[];
    onUpdate: (section: PageSection) => void;
    onRemove: () => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
}> = ({
    section,
    index,
    totalSections,
    disabled,
    defaultLayout,
    allowedBlockTypes,
    onUpdate,
    onRemove,
    onMoveUp,
    onMoveDown,
}) => {
    const et = useElementUITranslations();
    const [isExpanded, setIsExpanded] = React.useState(true);

    // Find current layout preset
    const getCurrentLayoutPreset = (): LayoutPresetKey => {
        const layout = section.layout;
        if (layout.type === 'single') return 'single';
        if (layout.type === 'vertical') return 'vertical';
        if (layout.type === 'horizontal') return 'horizontal';
        if (layout.type === 'grid') {
            if (layout.columns === 2) return 'grid2';
            if (layout.columns === 3) return 'grid3';
            if (layout.columns === 4) return 'grid4';
        }
        return 'vertical';
    };

    const handleLayoutChange = (preset: LayoutPresetKey) => {
        onUpdate({
            ...section,
            layout: sectionLayoutPresets[preset] as LayoutConfig,
        });
    };

    const handleTitleChange = (title: string) => {
        onUpdate({ ...section, title: title || undefined });
    };

    const addBlock = (type: BlockContentType = allowedBlockTypes?.[0] ?? 'rich-text') => {
        if (disabled) return;
        const newBlock = createBlock(generateId(), createDefaultBlockContent(type));
        onUpdate({
            ...section,
            blocks: [...section.blocks, newBlock],
        });
    };

    const updateBlock = (blockIndex: number, updatedBlock: PageBlock) => {
        if (disabled) return;
        const newBlocks = [...section.blocks];
        newBlocks[blockIndex] = updatedBlock;
        onUpdate({ ...section, blocks: newBlocks });
    };

    const removeBlock = (blockIndex: number) => {
        if (disabled) return;
        onUpdate({
            ...section,
            blocks: section.blocks.filter((_, i) => i !== blockIndex),
        });
    };

    const moveBlock = (fromIndex: number, direction: 'up' | 'down') => {
        if (disabled) return;
        const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
        if (toIndex < 0 || toIndex >= section.blocks.length) return;

        const newBlocks = [...section.blocks];
        [newBlocks[fromIndex], newBlocks[toIndex]] = [newBlocks[toIndex], newBlocks[fromIndex]];
        onUpdate({ ...section, blocks: newBlocks });
    };

    return (
        <Card className="page-edit-section-item overflow-hidden border-border-surface shadow-sm">
            {/* Section Header */}
            <div
                className="page-edit-section-header flex items-center gap-2 px-4 py-3 sm:px-5 bg-muted/50 cursor-pointer hover:bg-muted/70 transition-colors"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <GripVertical className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{et.section} {index + 1}</span>
                {section.title && (
                    <span className="text-sm text-muted-foreground">- {section.title}</span>
                )}
                <span className="text-xs text-muted-foreground">
                    ({section.blocks.length} blocks)
                </span>
                <div className="flex-1" />
                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                            e.stopPropagation();
                            onMoveUp();
                        }}
                        disabled={disabled || index === 0}
                    >
                        <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                            e.stopPropagation();
                            onMoveDown();
                        }}
                        disabled={disabled || index === totalSections - 1}
                    >
                        <ChevronDown className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove();
                        }}
                        disabled={disabled}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Section Content */}
            {isExpanded && (
                <CardContent className="page-edit-section-content pt-5 space-y-5 sm:pt-6">
                    {/* Section Settings */}
                    <div className="page-edit-section-settings grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor={`section-title-${section.id}`}>{et.sectionTitle}</Label>
                            <Input
                                id={`section-title-${section.id}`}
                                value={section.title || ''}
                                onChange={(e) => handleTitleChange(e.target.value)}
                                placeholder={et.sectionTitlePlaceholder}
                                disabled={disabled}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>{et.layout}</Label>
                            <Select
                                value={getCurrentLayoutPreset()}
                                onValueChange={(value) =>
                                    handleLayoutChange(value as LayoutPresetKey)
                                }
                                disabled={disabled}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {layoutOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            <div className="flex items-center gap-2">
                                                {option.icon}
                                                <span>{option.label}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Blocks */}
                    <div className="page-edit-section-blocks space-y-3">
                        <div className="page-edit-section-blocks-header flex items-center justify-between">
                            <Label>{et.contentBlocks}</Label>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => addBlock()}
                                disabled={disabled}
                            >
                                <Plus className="h-3 w-3 mr-1" />
                                Add Block
                            </Button>
                        </div>

                        {section.blocks.length === 0 ? (
                            <div className="page-edit-section-blocks-empty border border-dashed border-border-surface rounded-lg p-6 sm:p-8 text-center">
                                <p className="text-muted-foreground text-sm mb-3">
                                    No blocks yet. Add content to this section.
                                </p>
                                <div className="flex justify-center gap-2 flex-wrap">
                                    {(!allowedBlockTypes ||
                                        allowedBlockTypes.includes('rich-text')) && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => addBlock('rich-text')}
                                            disabled={disabled}
                                        >
                                            + Rich Text
                                        </Button>
                                    )}
                                    {(!allowedBlockTypes ||
                                        allowedBlockTypes.includes('image')) && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => addBlock('image')}
                                            disabled={disabled}
                                        >
                                            + Image
                                        </Button>
                                    )}
                                    {(!allowedBlockTypes ||
                                        allowedBlockTypes.includes('audio')) && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => addBlock('audio')}
                                            disabled={disabled}
                                        >
                                            + Audio
                                        </Button>
                                    )}
                                    {(!allowedBlockTypes ||
                                        allowedBlockTypes.includes('video')) && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => addBlock('video')}
                                            disabled={disabled}
                                        >
                                            + Video
                                        </Button>
                                    )}
                                    {(!allowedBlockTypes ||
                                        allowedBlockTypes.includes('office-document')) && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => addBlock('office-document')}
                                            disabled={disabled}
                                        >
                                            + Document
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="page-edit-block-list space-y-2">
                                {section.blocks.map((block, blockIndex) => (
                                    <BlockItem
                                        key={block.id}
                                        block={block}
                                        index={blockIndex}
                                        totalBlocks={section.blocks.length}
                                        disabled={disabled}
                                        allowedBlockTypes={allowedBlockTypes}
                                        onUpdate={(updatedBlock) =>
                                            updateBlock(blockIndex, updatedBlock)
                                        }
                                        onRemove={() => removeBlock(blockIndex)}
                                        onMoveUp={() => moveBlock(blockIndex, 'up')}
                                        onMoveDown={() => moveBlock(blockIndex, 'down')}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </CardContent>
            )}
        </Card>
    );
};

export const PageEdit: React.FC<PageEditProps> = ({
    value: data,
    onChange,
    disabled = false,
    hideSettings = false,
    allowedBlockTypes,
    config,
    language,
    className,
}) => {
    const { t } = useElementSchemaLocale(pageElementSchema.i18n, { config, language });
    const sections = data?.sections || [];
    const defaultLayout = data?.defaultLayout || sectionLayoutPresets.vertical;

    // Update data helper
    const updateData = (updates: Partial<PageElementData>) => {
        onChange({
            ...data,
            ...updates,
        });
    };

    // Add new section
    const addSection = () => {
        if (disabled) return;
        const newSection = createSection(generateId(), defaultLayout as LayoutConfig);
        updateData({
            sections: [...sections, newSection],
        });
    };

    // Update section
    const updateSection = (index: number, updatedSection: PageSection) => {
        if (disabled) return;
        const newSections = [...sections];
        newSections[index] = updatedSection;
        updateData({ sections: newSections });
    };

    // Remove section
    const removeSection = (index: number) => {
        if (disabled) return;
        updateData({
            sections: sections.filter((_, i) => i !== index),
        });
    };

    // Move section
    const moveSection = (fromIndex: number, direction: 'up' | 'down') => {
        if (disabled) return;
        const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
        if (toIndex < 0 || toIndex >= sections.length) return;

        const newSections = [...sections];
        [newSections[fromIndex], newSections[toIndex]] = [
            newSections[toIndex],
            newSections[fromIndex],
        ];
        updateData({ sections: newSections });
    };

    return (
        <div className={cn('page-edit space-y-6', className)}>
            {/* Page Settings - header info (title, description) */}
            {!hideSettings && (
                <div className="page-edit-header-form space-y-4 mb-4">
                    <div className="space-y-2">
                        <Label htmlFor="page-title">
                            {t('title')} <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="page-title"
                            value={data?.title || ''}
                            onChange={(e) => updateData({ title: e.target.value })}
                            placeholder={t('pageTitlePlaceholder')}
                            disabled={disabled}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="page-description">{t('pageDescriptionOptional')}</Label>
                        <Input
                            id="page-description"
                            value={data?.description || ''}
                            onChange={(e) => updateData({ description: e.target.value })}
                            placeholder={t('pageDescriptionPlaceholder')}
                            disabled={disabled}
                        />
                    </div>
                </div>
            )}

            {/* Sections */}
            <div className="page-edit-sections space-y-4">
                <div className="page-edit-sections-header flex items-center justify-between">
                    <h3 className="text-lg font-semibold">
                        {t('pageSections')} ({sections.length})
                    </h3>
                    <Button onClick={addSection} size="sm" disabled={disabled}>
                        <Plus className="h-4 w-4 mr-2" />
                        {t('addSection')}
                    </Button>
                </div>

                {sections.length === 0 ? (
                    <Card className="page-edit-sections-empty border-dashed border-border-surface">
                        <CardContent className="py-12 text-center">
                            <p className="text-muted-foreground mb-4">{t('pageNoSectionsYet')}</p>
                            <Button onClick={addSection} variant="outline" disabled={disabled}>
                                <Plus className="h-4 w-4 mr-2" />
                                {t('addSection')}
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="page-edit-section-list space-y-3">
                        {sections.map((section, index) => (
                            <SectionItem
                                key={section.id}
                                section={section}
                                index={index}
                                totalSections={sections.length}
                                disabled={disabled}
                                defaultLayout={defaultLayout as LayoutConfig}
                                allowedBlockTypes={allowedBlockTypes}
                                onUpdate={(updatedSection) => updateSection(index, updatedSection)}
                                onRemove={() => removeSection(index)}
                                onMoveUp={() => moveSection(index, 'up')}
                                onMoveDown={() => moveSection(index, 'down')}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PageEdit;
